import { CustomWidgetCollection, ElementFactory, Question, Serializer } from 'survey-core';
import { PropertyGridEditorCollection } from 'survey-creator-core';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { SurveyCreator } from 'survey-creator-knockout';
import { installQuillNbspFix } from '../../../../quill/quill-nbsp-fix';

installQuillNbspFix();

export function injectQuillSurveyCreatorKnockout(): void {
  const componentName = 'quill';

  //init once only
  if (Serializer.findClass(componentName)) {
    return;
  }

  const widget = {
    name: componentName,
    title: 'Quill',
    widgetIsLoaded: () => Quill !== undefined,
    isFit: (question: Question) => question.getType() === componentName,
    activatedByChanged: (activatedBy: string) => {
      Serializer.addClass(componentName, [], undefined, 'empty');
      let registerQuestion = ElementFactory.Instance.registerCustomQuestion;
      if (!!registerQuestion) {
        registerQuestion(componentName);
      }
      Serializer.addProperty(componentName, {
        name: 'height',
        default: '200px',
        category: 'layout',
      });
    },
    htmlTemplate: '<div style="color: var(--ctr-editor-content-text-color, var(--sjs-general-forecolor, var(--foreground, #161616)))"></div>',
    afterRender: (question: Question, el: HTMLElement) => {
      if (question.obj.customWasImported) {
        el.innerHTML = 'Not editable. Question was imported.';
        return; //no editor for imported HTML, quill would break the imported HTML
      }

      el.style.height = question.height;
      const quillOptions = {
        theme: 'snow',
        modules: {
          toolbar: [[{ 'header': [1, 2, 3, false] }], ['bold', 'italic', 'underline', 'link'], [{ 'list': 'ordered' }, { 'list': 'bullet' }], ['clean']],
        },
      };
      const editor = new Quill(el, quillOptions);
      editor.enable(!question.isReadOnly);
      let isValueChanging = false;
      editor.on('text-change', function (eventName, ...args) {
        isValueChanging = true;
        question.value = editor.getSemanticHTML();
        isValueChanging = false;
      });
      const updateValueHandler = function () {
        if (isValueChanging) {
          return;
        }
        const delta = editor.clipboard.convert({ html: question.value || '' });
        editor.setContents(delta, 'api');
      };
      question.valueChangedCallback = updateValueHandler;
      question.readOnlyChangedCallback = function () {
        editor.enable(!question.isReadOnly);
      };
      updateValueHandler();
    },
    willUnmount: (question: Question, el: HTMLElement) => {
      el.previousSibling && el.previousSibling.remove();
      el.innerHTML = '';
    },
  };

  CustomWidgetCollection.Instance.addCustomWidget(widget, 'customtype');

  // Register `quill` as an editor for properties of the `text` and `html` types in the Survey Creator's Property Grid
  PropertyGridEditorCollection.register({
    fit: function (prop) {
      return prop.type == 'html';
    },
    getJSON: function () {
      return { type: componentName };
    },
  });
}

export function injectQuillSurveyCreatorInstanceKnockout(creator: SurveyCreator): void {
  function applyHtml(_, options) {
    let str = options.text;
    if (str.indexOf('<p>') === 0) {
      // Remove root paragraphs <p></p>
      str = str.substring(3);
      str = str.substring(0, str.length - 4);
    }
    // Set HTML markup to render
    options.html = str;
  }

  //remove quill question type from toolbox
  const toolboxItems = creator.toolbox.items;
  const quillIndex = toolboxItems.findIndex((item) => item.name === 'quill');
  toolboxItems.splice(quillIndex, 1);

  // Apply HTML markup to survey contents
  creator.survey.onTextMarkdown.add(applyHtml);
  creator.onSurveyInstanceCreated.add((_, options) => {
    options.survey.onTextMarkdown.add(applyHtml);
  });
}


